import { CalendarEvent } from '../types/calendar';

/**
 * Get the first day of the month for a given date
 */
export const getFirstDayOfMonth = (dateStr: string): Date => {
  const date = new Date(dateStr);
  return new Date(date.getFullYear(), date.getMonth(), 1);
};

/**
 * Get the last day of the month for a given date
 */
export const getLastDayOfMonth = (dateStr: string): Date => {
  const date = new Date(dateStr);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
};

/**
 * Get the number of days in a month
 */
export const getDaysInMonth = (dateStr: string): number => {
  const lastDay = getLastDayOfMonth(dateStr);
  return lastDay.getDate();
};

/**
 * Get the day of week (0-6, Sunday = 0) for the first day of the month
 */
export const getFirstDayOfWeek = (dateStr: string): number => {
  const firstDay = getFirstDayOfMonth(dateStr);
  return firstDay.getDay();
};

/**
 * Get an array of dates for the calendar grid (including padding from prev/next month)
 */
export const getCalendarDates = (dateStr: string): Date[] => {
  const firstDay = getFirstDayOfMonth(dateStr);
  const lastDay = getLastDayOfMonth(dateStr);
  const firstDayOfWeek = firstDay.getDay();
  const daysInMonth = lastDay.getDate();

  const dates: Date[] = [];

  // Add padding days from previous month
  const prevMonthLastDay = new Date(firstDay);
  prevMonthLastDay.setDate(0);
  const prevMonthDays = prevMonthLastDay.getDate();

  for (let i = firstDayOfWeek - 1; i >= 0; i--) {
    dates.push(new Date(firstDay.getFullYear(), firstDay.getMonth() - 1, prevMonthDays - i));
  }

  // Add current month days
  for (let i = 1; i <= daysInMonth; i++) {
    dates.push(new Date(firstDay.getFullYear(), firstDay.getMonth(), i));
  }

  // Add padding days from next month to complete the grid (6 weeks = 42 days)
  const remainingDays = 42 - dates.length;
  for (let i = 1; i <= remainingDays; i++) {
    dates.push(new Date(firstDay.getFullYear(), firstDay.getMonth() + 1, i));
  }

  return dates;
};

/**
 * Format a date to ISO string (YYYY-MM-DD)
 */
export const formatDateToISO = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a date for display (e.g., "Mon, Jan 15 2024")
 */
export const formatDateForDisplay = (dateStr: string): string => {
  // Parse ISO date string (YYYY-MM-DD) as local date to avoid timezone issues
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format month and year (e.g., "January 2024")
 */
export const formatMonthYear = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long'
  });
};

/**
 * Get the start of the week for a given date (Sunday)
 */
export const getStartOfWeek = (dateStr: string): Date => {
  const date = new Date(dateStr);
  const day = date.getDay();
  const diff = date.getDate() - day;
  return new Date(date.getFullYear(), date.getMonth(), diff);
};

/**
 * Get the end of the week for a given date (Saturday)
 */
export const getEndOfWeek = (dateStr: string): Date => {
  const date = new Date(dateStr);
  const day = date.getDay();
  const diff = date.getDate() + (6 - day);
  return new Date(date.getFullYear(), date.getMonth(), diff);
};

/**
 * Get an array of 7 dates for the week view
 */
export const getWeekDates = (dateStr: string): Date[] => {
  const startOfWeek = getStartOfWeek(dateStr);
  const dates: Date[] = [];

  for (let i = 0; i < 7; i++) {
    const date = new Date(startOfWeek);
    date.setDate(startOfWeek.getDate() + i);
    dates.push(date);
  }

  return dates;
};

/**
 * Check if two dates are the same day
 */
export const isSameDay = (date1: string | Date, date2: string | Date): boolean => {
  const d1 = typeof date1 === 'string' ? new Date(date1) : date1;
  const d2 = typeof date2 === 'string' ? new Date(date2) : date2;

  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
};

/**
 * Check if a date is today
 */
export const isToday = (dateStr: string | Date): boolean => {
  const date = typeof dateStr === 'string' ? new Date(dateStr) : dateStr;
  const today = new Date();
  return isSameDay(date, today);
};

/**
 * Check if a date is in the current month
 */
export const isCurrentMonth = (date: Date, currentDateStr: string): boolean => {
  const currentDate = new Date(currentDateStr);
  return date.getMonth() === currentDate.getMonth() && date.getFullYear() === currentDate.getFullYear();
};

/**
 * Filter events for a specific date
 */
export const getEventsForDate = (events: CalendarEvent[], dateStr: string): CalendarEvent[] => {
  return events.filter(event => {
    const eventStart = new Date(event.startDate);
    const eventEnd = new Date(event.endDate);
    const targetDate = new Date(dateStr);

    // Check if the target date falls within the event's date range
    return targetDate >= eventStart && targetDate <= eventEnd;
  });
};

/**
 * Sort events by start time
 */
export const sortEventsByTime = (events: CalendarEvent[]): CalendarEvent[] => {
  return [...events].sort((a, b) => {
    // All-day events come first
    if (a.allDay && !b.allDay) return -1;
    if (!a.allDay && b.allDay) return 1;

    // Sort by start time if both have times
    if (a.startTime && b.startTime) {
      return a.startTime.localeCompare(b.startTime);
    }

    return 0;
  });
};

/**
 * Format time for display (e.g., "9:00 AM")
 */
export const formatTime = (timeStr: string): string => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`;
};

/**
 * Get a formatted time range (e.g., "9:00 AM - 10:00 AM")
 */
export const formatTimeRange = (startTime: string, endTime: string): string => {
  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};
